import dayjs from 'dayjs';
import 'dayjs/locale/fr-ca';
import 'dayjs/locale/en-ca';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';

// Extend dayjs with necessary plugins
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(LocalizedFormat);

export type DateFormatType = 'short' | 'long' | 'relative' | 'custom';

export interface I18nDateFormatterOptions {
  locale: string;
  timezone?: string;
}

/**
 * Formats a date according to the specified locale and format type.
 * 
 * @param date - The date to format
 * @param format - The format type: 'short', 'long', 'relative', or 'custom'
 * @param locale - The locale to use (e.g., 'en', 'en-CA', 'fr', 'fr-CA')
 * @param customFormat - Optional custom format string (used when format is 'custom')
 * @returns Formatted date string
 * 
 * @example
 * formatI18nDate(new Date(), 'long', 'fr-CA') // "15 octobre 2025 à 14:30"
 * formatI18nDate(new Date(), 'short', 'en-CA') // "October 15, 2025"
 * formatI18nDate(new Date(), 'relative', 'fr-CA') // "il y a 2 heures"
 * formatI18nDate(new Date(), 'custom', 'en', 'MMM DD, YYYY') // "Oct 15, 2025"
 */
export function formatI18nDate(
  date: Date | string | number,
  format: DateFormatType = 'long',
  locale: string = 'en',
  customFormat?: string
): string {
  // Normalize locale to dayjs format (convert 'en-CA' to 'en-ca')
  const dayjsLocale = locale.toLowerCase();
  dayjs.locale(dayjsLocale);
  
  const dateObj = dayjs(date);

  switch (format) {
    case 'relative':
      return dateObj.fromNow();
      
    case 'short': {
      // Use Intl.DateTimeFormat for consistent short format across locales
      const shortOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };
      return new Intl.DateTimeFormat(locale, shortOptions).format(dateObj.toDate());
    }
      
    case 'long': {
      // Use Intl.DateTimeFormat for consistent long format across locales
      const longOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: !locale.startsWith('fr'), // French uses 24-hour format
      };
      return new Intl.DateTimeFormat(locale, longOptions).format(dateObj.toDate());
    }
      
    case 'custom':
      if (!customFormat) {
        throw new Error('Custom format string is required when format type is "custom"');
      }
      return dateObj.format(customFormat);
      
    default:
      return dateObj.format();
  }
}

/**
 * Formats a date to a localized string using dayjs format tokens.
 * Useful when you need full control over the format.
 * 
 * @param date - The date to format
 * @param template - The dayjs format template (e.g., 'MMM DD, YYYY', 'ddd. MMM D')
 * @param locale - The locale to use
 * @returns Formatted date string
 * 
 * @example
 * formatI18nDateWithTemplate(new Date(), 'MMM DD, YYYY', 'fr-CA') // "oct. 15, 2025"
 * formatI18nDateWithTemplate(new Date(), 'ddd. MMM D', 'en-CA') // "Wed. Oct 15"
 * 
 * @see https://day.js.org/docs/en/display/format
 */
export function formatI18nDateWithTemplate(
  date: Date | string | number,
  template: string,
  locale: string = 'en'
): string {
  const dayjsLocale = locale.toLowerCase();
  dayjs.locale(dayjsLocale);
  return dayjs(date).format(template);
}

/**
 * Gets the user's current timezone.
 * 
 * @returns IANA timezone string (e.g., 'America/Toronto')
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Formats time only (no date) according to locale conventions.
 * 
 * @param date - The date/time to format
 * @param locale - The locale to use
 * @param includeSeconds - Whether to include seconds in the output
 * @returns Formatted time string
 * 
 * @example
 * formatI18nTime(new Date(), 'en-CA') // "2:30 PM"
 * formatI18nTime(new Date(), 'fr-CA') // "14:30"
 * formatI18nTime(new Date(), 'en-CA', true) // "2:30:45 PM"
 */
export function formatI18nTime(
  date: Date | string | number,
  locale: string = 'en',
  includeSeconds: boolean = false
): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' }),
    hour12: !locale.startsWith('fr'),
  };
  
  return new Intl.DateTimeFormat(locale, options).format(new Date(date));
}

